/**
 * usePipe — дашборд «Клиенты & ПАЙП»: воронка продаж и выполнение финплана
 * из Bitrix24 (read-only). Контракт — server/src/crm/pipe.ts (GET /api/crm/pipe).
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';

export interface PipeKpis {
  newLeads: number;
  nqlPlus: number;
  inWork: number;
  pipe: number;
  weighted: number;
  won: number;
  wonCount: number;
  planDonePct: number | null;
}
export interface FunnelStep {
  key: string;
  label: string;
  count: number;
  convPrev: number | null;
  convNql: number | null;
}
export interface ChannelRow {
  id: string;
  label: string;
  nqlPlus: number;
  wonCount: number;
  won: number;
  avgCheck: number | null;
  nqlToWon: number | null;
}
export interface MonthRow {
  month: string;
  wonCount: number;
  won: number;
  avgCheck: number | null;
  shareWon: number | null;
  forecast: number;
}
export interface WeeklyPoint {
  week: string;
  label: string;
  leads: number;
  /** Дошли до MQL и дальше (reachedSort ≥ 20) — «качественная» кривая. */
  mqlPlus: number;
  /** Детализация по дням недели (Пн→Вс), 7 элементов. */
  days: { label: string; leads: number; mqlPlus: number }[];
}
export interface RevenuePoint {
  month: string;
  plan: number;
  fact: number;
  forecast: number;
}
export interface PipeInvoiceView {
  name: string;
  amount: number;
  dueDate: string;
  month: string;
  paid: boolean;
  /** Счёт на стадии «Отправлен клиенту» (:S) — «ожидаемые деньги». */
  sent: boolean;
}
export interface PipeDealRow {
  id: string;
  company: string;
  assignee: string;
  month: string;
  stageName: string;
  opportunity: number;
  weighted: number;
  /** Итого по счетам этой сделки за выбранный месяц (по сроку оплаты). */
  invoiceTotal: number;
  /** «Ожидаемые деньги» — счета «Отправлен клиенту» + «Оплачен» за месяц. */
  expected: number;
  /** Оплаченная часть «ожидаемых» — для зелёной подсветки. */
  expectedPaid: number;
  comment: string;
  commentDate: string;
  status: string;
  invoices: PipeInvoiceView[];
  splitNote: string;
  /** «Переезд»: план месяца X (поле «Месяц» карточки) → факт месяца Y (оплата). */
  movedNote: string;
}

/** Строка план-факта: план — ручной ввод, факт — оплаченные счета всех каналов. */
export interface PlanFactRow {
  month: string;
  monthIdx: number;
  plan: number;
  fact: number;
  delta: number;
  donePct: number | null;
}

export interface ProductItem {
  company: string;
  product: string;
  cat: string;
  amount: number;
  dueDate: string;
  paid: boolean;
}
export interface ProductMonth {
  month: string;
  byCat: Record<string, number>;
  total: number;
  items: ProductItem[];
}
export interface PipeResponse {
  configured: boolean;
  year?: number;
  plan?: number;
  asOf?: string;
  channelOptions?: { id: string; label: string }[];
  monthOptions?: string[];
  filters?: { channels: string[]; months: string[] };
  moneyScoped?: boolean;
  kpis?: PipeKpis;
  productCats?: string[];
  productMonths?: ProductMonth[];
  productTotals?: { byCat: Record<string, number>; total: number };
  dealsHidden?: number;
  dealsTotal?: { budget: number; weighted: number; invoiceMonth: number; expected: number; expectedPaid: number };
  funnel?: FunnelStep[];
  channels?: ChannelRow[];
  months?: MonthRow[];
  qualification?: { label: string; count: number }[];
  weekly?: WeeklyPoint[];
  revenueCumulative?: RevenuePoint[];
  planFact?: PlanFactRow[];
  planFactTotals?: { plan: number; fact: number; delta: number; donePct: number | null };
  deals?: PipeDealRow[];
}

export interface PipeQuery {
  year: number;
  channels: string[];
  months: string[];
  plan?: number;
}

/** Сохранение плана месяца (ручной ввод на странице ПАЙП) + рефетч дашборда. */
export function useSavePipePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { year: number; month: number; amount: number }) =>
      apiFetch<{ ok: boolean }>('/api/crm/pipe/plan', { method: 'PUT', json: v }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['crm', 'pipe'] }),
  });
}

export function usePipe(q: PipeQuery) {
  const params = new URLSearchParams({ year: String(q.year) });
  if (q.channels.length) params.set('channels', q.channels.join(','));
  if (q.months.length) params.set('months', q.months.join(','));
  if (q.plan) params.set('plan', String(q.plan));
  const key = `${q.year}|${[...q.channels].sort().join(',')}|${[...q.months].sort().join(',')}|${q.plan ?? ''}`;
  return useQuery<PipeResponse>({
    queryKey: ['crm', 'pipe', key],
    queryFn: () => apiFetch<PipeResponse>(`/api/crm/pipe?${params.toString()}`),
    staleTime: 55_000,
    // Смена фильтров не сбрасывает таблицу в лоадер — старые цифры остаются
    // на экране с индикатором «обновляю…» (сервер отвечает из кэша мгновенно).
    placeholderData: keepPreviousData,
    // Тихий автоподхват свежего снапшота (сервер держит его тёплым, ответ <100мс).
    refetchInterval: 60_000,
  });
}
